import { MultiSelect, Select, Stack } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getMunicipiosByUf } from "@/services/ibge";
import { estadosData } from "@/modules/empresas/schema/endereco-schema";
import schema from "@/schema";
import { yup } from "@/schema/schema";
import { extractTextInputPropertiesFromFormSchema } from "@/utils/form";

const cidadeIdentifierFormSchema = schema.object({
  estado: schema.string().required().label("Estado"),
  cidade: schema.array().of(schema.string()).required().default([]).label("Cidade") // string().required().label("Estado")
});

type CidadeIdentifierFormType = yup.InferType<typeof cidadeIdentifierFormSchema>;

const defaultForm: CidadeIdentifierFormType = {
  estado: "",
  cidade: []
};

type Properties = {
  onChange: (cidade: string[]) => void;
};

/**
 *  Cidade Identifier Form
 * @param root0 - Properties
 * @param root0.onChange - On change cidade
 * @returns - Cidade Identifier Form
 */
export function CidadeIdentifierForm({ onChange }: Properties) {
  const form = useForm<CidadeIdentifierFormType>({
    initialValues: defaultForm,
    validate: yupResolver(cidadeIdentifierFormSchema),
    transformValues: (values) => cidadeIdentifierFormSchema.cast(values)
  });

  const estado = form.values.estado;
  const { data: municipios = [], isError: municipiosError } = useQuery({
    queryKey: ["municipios", estado],
    queryFn: () => getMunicipiosByUf(estado),
    enabled: !!estado,
  });

  useEffect(() => {
    if (municipiosError) {
      console.error("CidadeIdentifierForm: Falha ao carregar municípios");
    }
  }, [municipiosError]);

  useEffect(() => {
    const cidade = form.values.cidade;
    const cidadeString = Array.isArray(cidade) ? cidade.join(", ") : cidade;
    if (cidadeString) {
      onChange([cidadeString]);
    }
  }, [form.values.cidade]);

  const descriptor = cidadeIdentifierFormSchema.describe();

  const municipiosOptions = municipios.map((m) => ({ label: m.nome, value: m.nome }) as const);

  return (
    <Stack>
      <Select
        data={estadosData}
        {...form.getInputProps("estado")}
        {...extractTextInputPropertiesFromFormSchema(descriptor, "estado")}
      />
      <MultiSelect
        multiple
        data={municipiosOptions}
        searchable
        {...form.getInputProps("cidade")}
        {...extractTextInputPropertiesFromFormSchema(descriptor, "cidade")}
      />
    </Stack>
  );
}
